import React, { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { GridRenderEditCellParams } from "@mui/x-data-grid";
import { GridEditDateCell } from "./buyerListingGridEditDateCell";
import { DelayedReasonContent } from "./RevisedEcoeDateModal";
import { GridCellParams } from "@mui/x-data-grid";
import { Paper } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";

function isFutureFromToday(value: unknown): boolean {
  if (value == null) return false;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else {
    const str = String(value);
    // "YYYY-MM-DD" would parse as UTC midnight — append T12:00:00 so it's local noon instead
    d = /^\d{4}-\d{2}-\d{2}$/.test(str) ? new Date(str + "T12:00:00") : new Date(str);
  }
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dCopy = new Date(d.getTime());
  dCopy.setHours(0, 0, 0, 0);
  return dCopy.getTime() > today.getTime();
}

interface RevisedEcoeEditCellProps extends GridRenderEditCellParams {
  handleChangeDates: (id: string, field: string, date: string, params: GridRenderEditCellParams) => Promise<void>;
  handleChangeText: (id: string, field: string, value: string, params: GridCellParams) => Promise<void>;
}

/**
 * Revised ECOE edit flow:
 * 1. Date picker opens immediately when user edits the cell.
 * 2. User selects a FUTURE date → picker stays open, Delayed Reason panel appears (via portal).
 *    Backend is NOT updated yet.
 * 3. User selects a delayed reason and clicks Save → commit both to backend, close picker.
 * 4. User selects a past/today date → commit to backend immediately, close picker.
 */
export function RevisedEcoeEditCell(params: RevisedEcoeEditCellProps) {
  const { handleChangeDates, handleChangeText } = params;
  const apiRef = useGridApiContext();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Controlled open state: start open because we entered edit mode
  const [calendarOpen, setCalendarOpen] = useState(true);

  const pendingFutureDateRef = useRef<string | null>(null);
  const [showDelayedReasonPanel, setShowDelayedReasonPanel] = useState(false);

  /**
   * Portal container at document.body so the panel renders above all stacking
   * contexts (including the DatePicker Popper). Created once on mount.
   */
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    portalContainerRef.current = div;
    return () => {
      document.body.removeChild(div);
      portalContainerRef.current = null;
    };
  }, []);

  /**
   * While the Delayed Reason panel is visible, prevent cellFocusOut from propagating
   * to cellEditStop (which would call stopCellEditMode and unmount this component).
   *
   * The DataGrid's handleDocumentClick fires on every document mouseup. When the user
   * clicks the Autocomplete dropdown listbox (a Portal at document.body, outside the
   * cell's DOM), it publishes cellFocusOut → handleCellFocusOut → publishEvent('cellEditStop').
   *
   * Fix: subscribe to cellFocusOut with isFirst:true so our handler fires BEFORE the
   * internal handleCellFocusOut. We add isPropagationStopped=()=>true to the event.
   * DataGrid's publishEvent checks isSyntheticEvent(e) && e.isPropagationStopped()
   * before emitting — finding both true, it returns early and never emits cellEditStop.
   */
  useEffect(() => {
    if (!showDelayedReasonPanel) return;

    const unsubscribe = apiRef.current.subscribeEvent(
      "cellFocusOut",
      (cellParams, event) => {
        if (
          String(cellParams.id) !== String(params.row.id) ||
          cellParams.field !== params.field
        ) {
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event as any).isPropagationStopped = () => true;
      },
      { isFirst: true }
    );

    return unsubscribe;
  }, [showDelayedReasonPanel, apiRef, params.row.id, params.field]);

  /**
   * Called synchronously inside onAccept — before any async code runs.
   * If the date is future: mark it as pending, show the panel, return false
   * so GridEditDateCell skips handleClose (no stopCellEditMode, no backend call).
   * If the date is past/today: clear any existing delayedReason, then let
   * GridEditDateCell commit the date normally.
   */
  const interceptAccept = useCallback(
    (dateStr: string): boolean => {
      if (!isFutureFromToday(dateStr)) {
        pendingFutureDateRef.current = null;
        if (params.row.delayedReason) {
          handleChangeText(params.row.id, "delayedReason", "", params);
        }
        return true; // Normal date — let GridEditDateCell commit and close
      }
      pendingFutureDateRef.current = dateStr;
      setShowDelayedReasonPanel(true);
      return false; // Future date — block commit, keep picker open, show panel
    },
    [params, handleChangeText]
  );

  /**
   * Safety guard for the onBlur path in GridEditDateCell.
   * Prevents handleClose from committing while the delayed reason panel is visible.
   */
  const onBeforeAccept = useCallback((_dateStr: string): boolean => {
    return pendingFutureDateRef.current === null;
  }, []);

  /**
   * Called when the picker wants to close (e.g. ClickAwayListener outside calendar).
   * Keep picker open if a future date is still pending a delayed reason.
   */
  const onRequestClose = useCallback(() => {
    if (pendingFutureDateRef.current !== null) {
      return; // Don't close — delayed reason not yet saved
    }
    setCalendarOpen(false);
  }, []);

  const handleCancelDelayedReason = useCallback(() => {
    pendingFutureDateRef.current = null;
    setShowDelayedReasonPanel(false);
    setCalendarOpen(false);
    apiRef.current.stopCellEditMode({
      id: params.row.id,
      field: params.field,
      ignoreModifications: true,
    });
  }, [apiRef, params.row.id, params.field]);

  const handleSaveDelayedReason = useCallback(
    (delayedReason: string) => {
      const dateToCommit = pendingFutureDateRef.current;
      if (dateToCommit == null) return;
      handleChangeText(params.row.id, "delayedReason", delayedReason, params);
      handleChangeDates(params.row.id, params.field, dateToCommit, params);
      pendingFutureDateRef.current = null;
      setShowDelayedReasonPanel(false);
      setCalendarOpen(false);
      apiRef.current.stopCellEditMode({ id: params.row.id, field: params.field });
    },
    [params, handleChangeText, handleChangeDates, apiRef]
  );

  const handleChangeDatesForCell = useCallback(
    (id: string, field: string, date: string, _cellParams?: unknown) =>
      handleChangeDates(id, field, date, params),
    [handleChangeDates, params]
  );

  // Compute position at render time — no separate state that could be stale
  const rect = wrapperRef.current?.getBoundingClientRect();

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", width: "100%", minWidth: 120 }}
      data-testid="revised-ecoe-edit-cell"
    >
      <GridEditDateCell
        id={params.row.id}
        field={params.field}
        value={params.value}
        params={params}
        handleChangeDates={handleChangeDatesForCell}
        controlledOpen={calendarOpen}
        onRequestClose={onRequestClose}
        interceptAccept={interceptAccept}
        onBeforeAccept={onBeforeAccept}
      />
      {showDelayedReasonPanel && portalContainerRef.current &&
        createPortal(
          /*
           * position:fixed on a portal container at document.body is correctly
           * relative to the viewport — no CSS-transform ancestor interferes.
           * zIndex 9999 sits above the DatePicker Popper (~1300) and any overlay.
           */
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              top: (rect?.bottom ?? 0) + 4,
              left: rect?.left ?? 0,
              p: 2,
              borderRadius: 1,
              minWidth: 320,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <DelayedReasonContent
              onClose={handleCancelDelayedReason}
              onSave={handleSaveDelayedReason}
              initialDelayedReason={params.row.delayedReason ?? ""}
            />
          </Paper>,
          portalContainerRef.current
        )
      }
    </div>
  );
}
